<script setup lang="ts">
import type { StepEntity, VOnboardingWrapperOptions } from 'v-onboarding'
import { VOnboardingStep, VOnboardingWrapper } from 'v-onboarding'
import { useI18n } from 'vue-i18n'
import 'v-onboarding/dist/style.css'

interface Props {
  steps: StepEntity[]
  options?: VOnboardingWrapperOptions
  prevLabel?: string
  nextLabel?: string
  finishLabel?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (event: 'finish'): void
}>()

const { t } = useI18n()

const prevLabel = computed(() => props.prevLabel ?? t('onboarding.common.previous'))
const nextLabel = computed(() => props.nextLabel ?? t('onboarding.common.next'))
const finishLabel = computed(() => props.finishLabel ?? t('onboarding.common.finish'))

const currentStepNumber = ref(1)
const totalSteps = computed(() => props.steps.length || 1)

interface VOnboardingWrapperExposed {
  start: () => void
  finish: () => void
  goToStep: (newStepNumber: number | ((currentStepNumber: number) => number)) => void
}

const wrapperRef = ref<VOnboardingWrapperExposed | null>(null)

const mergedOptions = computed<VOnboardingWrapperOptions>(() => {
  return {
    ...props.options,
    scrollToStep: {
      enabled: true,
      options: { block: 'center', inline: 'center', behavior: 'smooth' },
      ...(props.options?.scrollToStep ?? {}),
    },
    popper: {
      strategy: 'fixed',
      placement: 'bottom',
      modifiers: [
        { name: 'offset', options: { offset: [0, 10] } },
        { name: 'flip', options: { fallbackPlacements: ['top'], padding: 8 } },
        { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8, tether: true } },
      ],
      ...(props.options?.popper ?? {}),
    },
    overlay: {
      enabled: true,
      preventOverlayInteraction: false,
      ...(props.options?.overlay ?? {}),
    },
  }
})

function refreshOverlay() {
  window.dispatchEvent(new Event('resize'))
  window.dispatchEvent(new Event('scroll'))
}

async function handleNavigate(navigate: () => void) {
  navigate()
  await nextTick()
  window.requestAnimationFrame(() => {
    refreshOverlay()
  })
  setTimeout(() => {
    refreshOverlay()
  }, 50)
}

function start() {
  currentStepNumber.value = 1
  wrapperRef.value?.start()
}

function finish() {
  wrapperRef.value?.finish()
}

function goToStep(newStepNumber: number | ((currentStepNumber: number) => number)) {
  const resolved = typeof newStepNumber === 'function'
    ? newStepNumber(currentStepNumber.value - 1)
    : newStepNumber

  currentStepNumber.value = (resolved ?? 0) + 1
  wrapperRef.value?.goToStep(newStepNumber)
}

function handlePrevious(navigate: () => void) {
  currentStepNumber.value = Math.max(1, currentStepNumber.value - 1)
  handleNavigate(navigate)
}

function handleNext(navigate: () => void) {
  currentStepNumber.value = Math.min(totalSteps.value, currentStepNumber.value + 1)
  handleNavigate(navigate)
}

defineExpose({
  start,
  finish,
  goToStep,
})
</script>

<template>
  <VOnboardingWrapper
    ref="wrapperRef"
    :steps="props.steps"
    :options="mergedOptions"
    @finish="emit('finish')"
  >
    <template #default="{ previous, next, step, isFirst, isLast }">
      <VOnboardingStep>
        <div
          v-if="step && step.content"
          class="onboarding-step"
        >
          <div class="onboarding-header">
            <h3>{{ step.content.title }}</h3>
            <SolButton
              id="onboarding-skip-all"
              variant="tertiary"
              size="small"
              type="button"
              class="onboarding-skip-all"
              @click="finish"
            >
              {{ t('onboarding.common.skip_all') }}
            </SolButton>
          </div>
          <span class="onboarding-description">
            {{ step.content.description }}
          </span>
          <div
            v-if="totalSteps > 1"
            class="onboarding-progress"
          >
            {{ currentStepNumber }} de {{ totalSteps }}
          </div>
          <div class="onboarding-navigation">
            <SolButton
              v-if="!isFirst"
              id="onboarding-prev"
              type="button"
              variant="secondary"
              size="small"
              @click="handlePrevious(previous)"
            >
              {{ prevLabel }}
            </SolButton>
            <SolButton
              v-if="!isLast"
              id="onboarding-next"
              type="button"
              variant="primary"
              class="onboarding-button-primary-green"
              size="small"
              @click="handleNext(next)"
            >
              {{ nextLabel }}
            </SolButton>
            <SolButton
              v-if="isLast"
              id="onboarding-finish"
              type="button"
              variant="primary"
              class="onboarding-button-primary-green"
              size="small"
              @click="finish"
            >
              {{ finishLabel }}
            </SolButton>
          </div>
        </div>
      </VOnboardingStep>
    </template>
  </VOnboardingWrapper>
</template>

<style lang="scss">
/**
 * v-onboarding renders overlay parts in the document body (teleport),
 * so z-index variables must be global (scoped styles won't match reliably).
 */
:root {
  --v-onboarding-overlay-z: 20000;
  --v-onboarding-step-z: 20001;
}
</style>

<style scoped lang="scss">
:deep(.onboarding-step) {
  @apply p-4 bg-neutral-high-pure rounded-lg;
  @apply flex flex-col gap-1;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  width: 360px;

  .onboarding-header {
    @apply flex items-center justify-between gap-2;
    @apply mb-2;

    h3 {
      @apply fonts-heading-h4 text-neutral-low-dark;
      @apply m-0;
    }

    .onboarding-skip-all {
      @apply fonts-body-small-regular text-neutral-low-medium;
      @apply bg-transparent border-none cursor-pointer;
      @apply hover:text-neutral-low-dark;
      @apply transition-colors;
      padding: 0;
      text-decoration: underline;
      white-space: nowrap;
      flex-shrink: 0;
    }
  }

  .onboarding-description {
    @apply fonts-body-medium-regular text-neutral-low-medium self-stretch;
  }

  .onboarding-progress {
    @apply fonts-body-small-regular text-neutral-low-medium;
    @apply mt-4;
  }

  .onboarding-navigation {
    @apply flex gap-2 justify-end;
    @apply mt-4;
  }
}

:deep(.onboarding-button-primary-green) {
  background-color: rgba(0, 181, 105, 1) !important;
  border-color: rgba(0, 181, 105, 1) !important;
  color: rgba(255, 255, 255, 1) !important;
}

:deep(.onboarding-button-primary-green:hover) {
  background-color: rgba(0, 136, 89, 1) !important;
  border-color: rgba(0, 136, 89, 1) !important;
}

/* Prevent overlay from capturing scroll; only the step is interactive. */
:global(.v-onboarding__overlay) {
  position: fixed !important;
  inset: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  overflow: hidden !important;
  pointer-events: none !important;
  overscroll-behavior: none !important;
}

:global(.v-onboarding__wrapper) {
  position: fixed !important;
  inset: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  overflow: hidden !important;
  pointer-events: none !important;
  overscroll-behavior: none !important;
}

:global(.v-onboarding__step) {
  pointer-events: auto !important;
  overflow: visible !important;
  overscroll-behavior: contain !important;
}
</style>
